import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, BarChart3 } from "lucide-react";
import { apiUrl } from "./LoginSignup";
import Loader from "./Loader";
import "./LeadProductivity.css";

const SRDEV_ROLE = "srdev";

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7", label: "Last 7 days" },
  { id: "month", label: "Current month" },
  { id: "lastMonth", label: "Last month" },
  { id: "custom", label: "Date range" },
];

const KEY_STATUSES = [
  "new",
  "answered",
  "call back",
  "interested",
  "qualified",
  "not interested",
  "dnd",
];

const pct = (part, whole) =>
  whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;

const LeadProductivity = () => {
  const userSession = useMemo(
    () => JSON.parse(localStorage.getItem("userSession")) || {},
    []
  );
  const isSrdev = userSession.user_role === SRDEV_ROLE;
  const authHeaders = useMemo(
    () => ({ authorization: userSession.token }),
    [userSession.token]
  );

  const [preset, setPreset] = useState("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [bdmId, setBdmId] = useState("");
  const [bdmList, setBdmList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedBdm, setExpandedBdm] = useState(null);
  const [expandedScheme, setExpandedScheme] = useState(null);

  const fetchBdmList = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/user/bdm-list`, { headers: authHeaders });
      setBdmList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBdmList([]);
    }
  }, [authHeaders]);

  const fetchProductivity = useCallback(async () => {
    if (!isSrdev) return;
    if (preset === "custom" && (!dateFrom || !dateTo)) {
      setError("Select both From and To dates for a custom range");
      setData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const params = { preset };
      if (preset === "custom") {
        params.dateFrom = dateFrom;
        params.dateTo = dateTo;
      }
      if (bdmId) params.bdmId = bdmId;

      const res = await axios.get(`${apiUrl}/leads/productivity`, {
        headers: authHeaders,
        params,
      });
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.message || "Failed to load BDM productivity");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, isSrdev, preset, dateFrom, dateTo, bdmId]);

  useEffect(() => {
    if (isSrdev) fetchBdmList();
  }, [isSrdev, fetchBdmList]);

  useEffect(() => {
    if (!isSrdev) return;
    if (preset === "custom") return;
    fetchProductivity();
  }, [isSrdev, preset, bdmId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSrdev || preset !== "custom" || !dateFrom || !dateTo) return;
    fetchProductivity();
  }, [bdmId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dailyByDay = useMemo(() => {
    if (!data?.daily?.length) return [];
    const map = {};
    data.daily.forEach((row) => {
      if (!map[row.day]) map[row.day] = { day: row.day, total: 0, byBdm: {} };
      map[row.day].total += row.assigned_count;
      map[row.day].byBdm[row.bdm_id] = {
        name: row.bdm_name,
        count: row.assigned_count,
      };
    });
    return Object.values(map).sort((a, b) => a.day.localeCompare(b.day));
  }, [data]);

  const statusEntries = useMemo(() => {
    const breakdown = data?.status_breakdown || {};
    const known = KEY_STATUSES.filter((s) => breakdown[s] != null);
    const extras = Object.keys(breakdown)
      .filter((s) => !KEY_STATUSES.includes(s))
      .sort((a, b) => breakdown[b] - breakdown[a]);
    return [...known, ...extras].map((status) => ({
      status,
      count: breakdown[status] || 0,
    }));
  }, [data]);

  const statusTotal = useMemo(
    () => statusEntries.reduce((sum, s) => sum + s.count, 0),
    [statusEntries]
  );

  const schemeBdmByScheme = useMemo(() => {
    const map = {};
    (data?.scheme_by_bdm || []).forEach((row) => {
      if (!map[row.scheme]) map[row.scheme] = [];
      map[row.scheme].push(row);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => b.interest_count - a.interest_count || b.total - a.total)
    );
    return map;
  }, [data]);

  if (!isSrdev) {
    return (
      <div className="lead-productivity">
        <div className="lp-alert lp-alert-error">
          Access denied. Only Sr Dev can view BDM lead productivity.
        </div>
      </div>
    );
  }

  const totals = data?.totals;
  const interestTotals = data?.scheme_interest_totals;

  return (
    <div className="lead-productivity">
      <div className="lp-header">
        <div>
          <h1>
            <BarChart3 size={22} /> BDM Lead Productivity
          </h1>
          <p className="lp-subtitle">
            Leads assigned, status mix, and scheme-wise interest (Indian Standard Time)
          </p>
        </div>
        <button
          type="button"
          className="lp-btn lp-btn-secondary"
          onClick={fetchProductivity}
          disabled={loading}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div className="lp-presets">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`lp-preset ${preset === p.id ? "active" : ""}`}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="lp-filters">
        {preset === "custom" && (
          <>
            <div className="lp-filter-group">
              <label>From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="lp-filter-group">
              <label>To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="lp-btn lp-btn-primary"
              onClick={fetchProductivity}
              disabled={loading || !dateFrom || !dateTo}
            >
              Apply
            </button>
          </>
        )}
        <div className="lp-filter-group">
          <label>BDM</label>
          <select value={bdmId} onChange={(e) => setBdmId(e.target.value)}>
            <option value="">All BDMs</option>
            {bdmList.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="lp-alert lp-alert-error">{error}</div>}

      {loading ? (
        <Loader />
      ) : data ? (
        <>
          <div className="lp-range-label">
            Showing <strong>{data.dateFrom}</strong>
            {data.dateFrom !== data.dateTo ? (
              <>
                {" "}
                to <strong>{data.dateTo}</strong>
              </>
            ) : null}{" "}
            <span className="lp-tz">({data.timezone})</span>
          </div>

          <div className="lp-summary">
            <div className="lp-stat">
              <span className="lp-stat-value">{totals?.assigned_count ?? 0}</span>
              <span className="lp-stat-label">Leads assigned</span>
            </div>
            <div className="lp-stat">
              <span className="lp-stat-value">{totals?.call_count ?? 0}</span>
              <span className="lp-stat-label">Calls logged</span>
            </div>
            <div className="lp-stat lp-stat-positive">
              <span className="lp-stat-value">
                {interestTotals?.interest_count ?? 0}
              </span>
              <span className="lp-stat-label">
                Interested + Qualified ({interestTotals?.interest_rate ?? 0}%)
              </span>
            </div>
            <div className="lp-stat">
              <span className="lp-stat-value">
                {totals?.bdms_with_assignments ?? 0}/{totals?.bdm_count ?? 0}
              </span>
              <span className="lp-stat-label">BDMs with assignments</span>
            </div>
          </div>

          <div className="lp-section">
            <h2>Status breakdown</h2>
            <p className="lp-section-note">
              Current lead status for leads assigned in this period
            </p>
            {statusEntries.length === 0 ? (
              <div className="lp-empty-block">No status data for this period</div>
            ) : (
              <div className="lp-status-grid">
                {statusEntries.map(({ status, count }) => {
                  const share = pct(count, statusTotal);
                  const tone =
                    status === "interested" || status === "qualified"
                      ? "positive"
                      : status === "not interested" || status === "dnd"
                        ? "negative"
                        : "neutral";
                  return (
                    <div key={status} className={`lp-status-card lp-tone-${tone}`}>
                      <div className="lp-status-card-top">
                        <span className="lp-status-name">{status}</span>
                        <span className="lp-status-count">{count}</span>
                      </div>
                      <div className="lp-status-meter">
                        <div style={{ width: `${Math.min(share, 100)}%` }} />
                      </div>
                      <span className="lp-status-share">{share}% of assigned</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lp-section">
            <h2>Scheme-wise interest analysis</h2>
            <p className="lp-section-note">
              Interest = Interested + Qualified. Expand a scheme to see BDM contribution.
            </p>
            <div className="lp-table-wrap">
              <table className="lp-table">
                <thead>
                  <tr>
                    <th>Scheme</th>
                    <th>Total</th>
                    <th>Interested</th>
                    <th>Qualified</th>
                    <th>Interest</th>
                    <th>Interest %</th>
                    <th>Not interested</th>
                    <th>Not %</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.scheme_interest || []).map((row) => {
                    const isOpen = expandedScheme === row.scheme;
                    const bdmRows = schemeBdmByScheme[row.scheme] || [];
                    return (
                      <React.Fragment key={row.scheme}>
                        <tr>
                          <td className="lp-bdm-name">{row.scheme}</td>
                          <td>
                            <strong>{row.total}</strong>
                          </td>
                          <td className="lp-pos">{row.interested}</td>
                          <td className="lp-pos">{row.qualified}</td>
                          <td>
                            <strong className="lp-pos">{row.interest_count}</strong>
                          </td>
                          <td>
                            <div className="lp-share">
                              <div
                                className="lp-share-bar lp-share-bar-pos"
                                style={{ width: `${Math.min(row.interest_rate, 100)}%` }}
                              />
                              <span>{row.interest_rate}%</span>
                            </div>
                          </td>
                          <td className="lp-neg">{row.not_interested}</td>
                          <td className="lp-neg">{row.not_interest_rate}%</td>
                          <td>
                            {bdmRows.length > 0 && (
                              <button
                                type="button"
                                className="lp-link-btn"
                                onClick={() =>
                                  setExpandedScheme(isOpen ? null : row.scheme)
                                }
                              >
                                {isOpen ? "Hide BDMs" : "By BDM"}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="lp-status-row">
                            <td colSpan={9}>
                              <div className="lp-nested-table-wrap">
                                <table className="lp-table lp-table-compact">
                                  <thead>
                                    <tr>
                                      <th>BDM</th>
                                      <th>Total</th>
                                      <th>Interested</th>
                                      <th>Qualified</th>
                                      <th>Interest</th>
                                      <th>Interest %</th>
                                      <th>Not interested</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {bdmRows.map((b) => (
                                      <tr key={`${row.scheme}-${b.bdm_id}`}>
                                        <td>{b.bdm_name}</td>
                                        <td>{b.total}</td>
                                        <td className="lp-pos">{b.interested}</td>
                                        <td className="lp-pos">{b.qualified}</td>
                                        <td>
                                          <strong className="lp-pos">{b.interest_count}</strong>
                                        </td>
                                        <td>{b.interest_rate}%</td>
                                        <td className="lp-neg">{b.not_interested}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {Object.keys(row.status_breakdown || {}).length > 0 && (
                                <div className="lp-status-chips" style={{ marginTop: 10 }}>
                                  {Object.entries(row.status_breakdown)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([status, count]) => (
                                      <span key={status} className="lp-chip">
                                        {status}: <strong>{count}</strong>
                                      </span>
                                    ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!data.scheme_interest?.length && (
                    <tr>
                      <td colSpan={9} className="lp-empty">
                        No scheme data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
                {data.scheme_interest?.length > 0 && interestTotals && (
                  <tfoot>
                    <tr className="lp-tfoot">
                      <td>Total</td>
                      <td>
                        <strong>{interestTotals.total}</strong>
                      </td>
                      <td className="lp-pos">{interestTotals.interested}</td>
                      <td className="lp-pos">{interestTotals.qualified}</td>
                      <td>
                        <strong className="lp-pos">{interestTotals.interest_count}</strong>
                      </td>
                      <td>{interestTotals.interest_rate}%</td>
                      <td className="lp-neg">{interestTotals.not_interested}</td>
                      <td>
                        {pct(interestTotals.not_interested, interestTotals.total)}%
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="lp-section">
            <h2>BDM performance</h2>
            <div className="lp-table-wrap">
              <table className="lp-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>BDM</th>
                    <th>Assigned</th>
                    <th>Interested</th>
                    <th>Qualified</th>
                    <th>Not interested</th>
                    <th>Calls</th>
                    <th>Share</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((row, idx) => {
                    const share =
                      totals?.assigned_count > 0
                        ? Math.round((row.assigned_count / totals.assigned_count) * 1000) / 10
                        : 0;
                    const isOpen = expandedBdm === row.bdm_id;
                    const statuses = Object.entries(row.status_breakdown || {});
                    const sb = row.status_breakdown || {};
                    return (
                      <React.Fragment key={row.bdm_id}>
                        <tr
                          className={
                            row.assigned_count === 0 && row.call_count === 0 ? "lp-muted" : ""
                          }
                        >
                          <td>{idx + 1}</td>
                          <td className="lp-bdm-name">{row.bdm_name}</td>
                          <td>
                            <strong>{row.assigned_count}</strong>
                          </td>
                          <td className="lp-pos">{sb.interested || 0}</td>
                          <td className="lp-pos">{sb.qualified || 0}</td>
                          <td className="lp-neg">{sb["not interested"] || 0}</td>
                          <td>{row.call_count}</td>
                          <td>
                            <div className="lp-share">
                              <div
                                className="lp-share-bar"
                                style={{ width: `${Math.min(share, 100)}%` }}
                              />
                              <span>{share}%</span>
                            </div>
                          </td>
                          <td>
                            {statuses.length > 0 && (
                              <button
                                type="button"
                                className="lp-link-btn"
                                onClick={() =>
                                  setExpandedBdm(isOpen ? null : row.bdm_id)
                                }
                              >
                                {isOpen ? "Hide" : "All statuses"}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="lp-status-row">
                            <td colSpan={9}>
                              <div className="lp-status-chips">
                                {statuses
                                  .sort((a, b) => b[1] - a[1])
                                  .map(([status, count]) => (
                                    <span key={status} className="lp-chip">
                                      {status}: <strong>{count}</strong>
                                    </span>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!data.rows?.length && (
                    <tr>
                      <td colSpan={9} className="lp-empty">
                        No BDM data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {dailyByDay.length > 1 && (
            <div className="lp-section">
              <h2>Daily assignments</h2>
              <div className="lp-table-wrap">
                <table className="lp-table lp-table-compact">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Total assigned</th>
                      <th>By BDM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyByDay.map((day) => (
                      <tr key={day.day}>
                        <td>{day.day}</td>
                        <td>
                          <strong>{day.total}</strong>
                        </td>
                        <td>
                          <div className="lp-status-chips">
                            {Object.entries(day.byBdm)
                              .sort((a, b) => b[1].count - a[1].count)
                              .map(([id, info]) => (
                                <span key={id} className="lp-chip">
                                  {info.name}: <strong>{info.count}</strong>
                                </span>
                              ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default LeadProductivity;
